import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { Module } from '@/modules/companies/consts'
import { useTranslate } from '@ur-solutions/react-hooks'
import { PeriodList } from './period-list'
import {
  SettingRow,
  SettingSwitch,
  SettingsSection,
} from './salary-settings-section'
import type {
  SalarySettingsForm,
  SalarySettingsTabProps,
} from './SalarySettingsFormView'

interface AddonChoice {
  toggle: 'useTimeAddons' | 'useTimeOverrides' | 'useFixedAddons'
  codes:
    | 'timeAddonSalaryCodes'
    | 'timeOverrideSalaryCodes'
    | 'fixedAddonSalaryCodes'
  label: string
}

export const SalarySettingsAddons: React.FC<SalarySettingsTabProps> = ({
  form,
  config,
}) => {
  const translations = useTranslate({
    addonChoices: 'settings.addon-choices',
    addonChoicesExplanation: 'settings.addon-choices-explanation',
    useTimeAddons: 'settings.use-time-addons',
    useTimeOverrides: 'settings.use-time-overrides',
    useFixedAddons: 'settings.use-fixed-addons',
    selectableSalaryCodes: 'settings.selectable-salary-codes',
    selectableSalaryCodesExplanation:
      'settings.selectable-salary-codes-explanation',
    addonBasedOnTimeOfDay: 'salary.addon-based-on-time-of-day',
  })
  const { formValues, updateForm } = form

  const choices: AddonChoice[] = [
    {
      toggle: 'useTimeAddons',
      codes: 'timeAddonSalaryCodes',
      label: translations.useTimeAddons,
    },
    {
      toggle: 'useTimeOverrides',
      codes: 'timeOverrideSalaryCodes',
      label: translations.useTimeOverrides,
    },
    {
      toggle: 'useFixedAddons',
      codes: 'fixedAddonSalaryCodes',
      label: translations.useFixedAddons,
    },
  ]

  return (
    <>
      <SettingsSection
        title={translations.addonChoices}
        description={translations.addonChoicesExplanation}
      >
        {choices.map(({ toggle, codes, label }) => (
          <div key={toggle}>
            <SettingSwitch
              label={label}
              checked={formValues[toggle]}
              onChange={checked =>
                updateForm({ [toggle]: checked } as Partial<SalarySettingsForm>)
              }
            />
            {formValues[toggle] && (
              <SettingRow
                className="pt-0"
                label={translations.selectableSalaryCodes}
                description={translations.selectableSalaryCodesExplanation}
              >
                <SalaryCodeCombobox
                  mode="multiple"
                  value={formValues[codes]}
                  onChange={selected => updateForm({ [codes]: selected })}
                />
              </SettingRow>
            )}
          </div>
        ))}
      </SettingsSection>

      {(config.name === Module.TIMESHEETS ||
        config.name === Module.ACTIVITIES) && (
        <SettingsSection title={translations.addonBasedOnTimeOfDay}>
          <div className="px-5 py-4 sm:px-6">
            <PeriodList form={form} field="periodAddons" />
          </div>
        </SettingsSection>
      )}
    </>
  )
}
